import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Select,
  MultiSelect,
  Paper,
  TextInput,
  NumberInput,
  JsonInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconFilter,
  IconRefresh,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  useMachinesAdmin,
  useCreateMachine,
  useUpdateMachine,
  useToggleMachineActive,
  useSetMachineOnlineStatus,
} from "../../../hooks/admin/useMachines";
import { useMachineCategories } from "../../../hooks/admin/useMachineCategories";
import { useVendorsAdmin } from "../../../hooks/admin/useVendors";
import { Machine } from "../../../services/admin/machines.service";

const ITEMS_PER_PAGE = 20;

interface MachineFilters {
  vendorIds?: string[];
  categoryIds?: string[];
}

export default function MachinesAdminPage() {
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  const [editing, setEditing] = useState<Machine | null>(null);
  const [itemToToggle, setItemToToggle] = useState<Machine | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<MachineFilters>({});
  const [pendingFilters, setPendingFilters] = useState<MachineFilters>({});
  const [page, setPage] = useState(1);

  const { data: machines = [], isLoading, refetch } = useMachinesAdmin();
  const { data: categories = [] } = useMachineCategories();
  const { data: vendors = [] } = useVendorsAdmin();
  const createMachine = useCreateMachine();
  const updateMachine = useUpdateMachine();
  const toggleMachineActive = useToggleMachineActive();
  const setOnlineStatus = useSetMachineOnlineStatus();

  const form = useForm({
    initialValues: {
      name: "",
      category_id: "",
      vendor_id: "",
      connection_type: "",
      connection_config: "{}",
      retry_attempts: 3,
    },
    validate: {
      name: (v: string) => (v.trim() ? null : "Name is required"),
    },
  });

  const vendorOptions = useMemo(
    () =>
      vendors
        .filter((v) => v.isActive !== false)
        .map((v) => ({ value: v.id, label: v.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [vendors]
  );

  const categoryOptions = useMemo(
    () =>
      categories
        .map((c) => ({ value: c.id, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [categories]
  );

  const allVendorOptions = useMemo(
    () =>
      vendors
        .map((v) => ({ value: v.id, label: v.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [vendors]
  );

  const allCategoryOptions = useMemo(
    () =>
      categories
        .map((c) => ({ value: c.id, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [categories]
  );

  const filteredData = useMemo(() => {
    let result = machines;

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.category?.name?.toLowerCase().includes(q) ||
          m.vendor?.name?.toLowerCase().includes(q) ||
          m.connectionType?.toLowerCase().includes(q)
      );
    }

    if (filters.vendorIds?.length) {
      result = result.filter(
        (m) => m.vendorId && filters.vendorIds!.includes(m.vendorId)
      );
    }

    if (filters.categoryIds?.length) {
      result = result.filter(
        (m) => m.categoryId && filters.categoryIds!.includes(m.categoryId)
      );
    }

    return result;
  }, [machines, debouncedSearch, filters]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  const hasActiveFilters =
    (filters.vendorIds && filters.vendorIds.length > 0) ||
    (filters.categoryIds && filters.categoryIds.length > 0);

  const handleAdd = () => {
    setEditing(null);
    form.reset();
    openFormModal();
  };

  const handleEdit = (item: Machine) => {
    setEditing(item);
    form.setValues({
      name: item.name,
      category_id: item.categoryId || "",
      vendor_id: item.vendorId || "",
      connection_type: item.connectionType || "",
      connection_config: JSON.stringify(item.connectionConfig || {}, null, 2),
      retry_attempts: item.retryAttempts,
    });
    openFormModal();
  };

  const handleSubmit = async (values: typeof form.values) => {
    let connectionConfig;
    try {
      connectionConfig = JSON.parse(values.connection_config);
    } catch {
      connectionConfig = {};
    }

    const submitData = {
      name: values.name,
      category_id: values.category_id || undefined,
      vendor_id: values.vendor_id || undefined,
      connection_type: values.connection_type || undefined,
      connection_config: connectionConfig,
      retry_attempts: values.retry_attempts,
    };

    if (editing) {
      await updateMachine.mutateAsync({ id: editing.id, ...submitData });
    } else {
      await createMachine.mutateAsync(submitData);
    }
    closeFormModal();
    form.reset();
  };

  const handleToggle = (item: Machine) => {
    setItemToToggle(item);
    openConfirmModal();
  };

  const confirmToggle = async () => {
    if (itemToToggle) {
      await toggleMachineActive.mutateAsync({
        id: itemToToggle.id,
        isActive: !itemToToggle.isActive,
      });
      closeConfirmModal();
      setItemToToggle(null);
    }
  };

  const handleToggleOnline = async (item: Machine) => {
    await setOnlineStatus.mutateAsync({
      id: item.id,
      isOnline: !item.isOnline,
    });
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({});
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Machines</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage internal staining machines including vendor and machine
                category assignments
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name, category, vendor, or connection type..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  >
                    Refresh
                  </Button>
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.vendorIds && filters.vendorIds.length > 0 && (
                      <Badge variant="light" color="cyan">
                        {filters.vendorIds.length} Vendor
                        {filters.vendorIds.length > 1 ? "s" : ""}
                      </Badge>
                    )}
                    {filters.categoryIds && filters.categoryIds.length > 0 && (
                      <Badge variant="light" color="violet">
                        {filters.categoryIds.length} Categor
                        {filters.categoryIds.length > 1 ? "ies" : "y"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} machine
                  {filteredData.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Machines ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No machines match the current filters"
                    : "No machines found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Category</Table.Th>
                      <Table.Th>Vendor</Table.Th>
                      <Table.Th>Connection Type</Table.Th>
                      <Table.Th>Online</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ opacity: item.isActive ? 1 : 0.5 }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={item.categoryId ? "violet" : "gray"}
                            variant="light"
                          >
                            {item.category?.name || "Uncategorized"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={item.vendorId ? "cyan" : "gray"}
                            variant="light"
                          >
                            {item.vendor?.name || "No Vendor"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {item.connectionType || (
                              <Text span c="dimmed">
                                —
                              </Text>
                            )}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={item.isOnline ? "green" : "red"}
                            variant="light"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleToggleOnline(item)}
                          >
                            {item.isOnline ? "Online" : "Offline"}
                          </Badge>
                        </Table.Td>
                        <Table.Td style={{ width: 100 }}>
                          <Badge
                            color={item.isActive ? "green" : "red"}
                            variant="light"
                            size="sm"
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>
                        <Table.Td style={{ width: 100 }}>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={item.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={item.isActive ? "red" : "green"}
                                onClick={() => handleToggle(item)}
                              >
                                {item.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editing ? "Edit Machine" : "Add New Machine"}
          size="lg"
          closeOnClickOutside={false}
        >
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Name"
                required
                withAsterisk
                {...form.getInputProps("name")}
              />
              <Select
                label="Category"
                placeholder="Select a category"
                data={categoryOptions}
                searchable
                clearable
                {...form.getInputProps("category_id")}
              />
              <Select
                label="Vendor"
                placeholder="Select a vendor"
                data={vendorOptions}
                searchable
                clearable
                {...form.getInputProps("vendor_id")}
              />
              <Select
                label="Connection Type"
                placeholder="Select connection type"
                data={[
                  { value: "neopath_pro", label: "NeoPATH Pro" },
                  { value: "leica_bond", label: "Leica BOND" },
                ]}
                clearable
                {...form.getInputProps("connection_type")}
              />
              <JsonInput
                label="Connection Config (JSON)"
                placeholder='{"ip": "192.168.1.100", "port": 8888}'
                minRows={4}
                {...form.getInputProps("connection_config")}
              />
              <NumberInput
                label="Retry Attempts"
                min={1}
                max={10}
                {...form.getInputProps("retry_attempts")}
              />

              <Group justify="flex-end" mt="md" gap="xs">
                <Button variant="default" onClick={closeFormModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createMachine.isPending || updateMachine.isPending}
                >
                  {editing ? "Update" : "Create"}
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Machines"
          size="md"
        >
          <Stack gap="md">
            <MultiSelect
              label="Vendor"
              placeholder="Select vendors..."
              data={allVendorOptions}
              value={pendingFilters.vendorIds || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  vendorIds: values.length > 0 ? values : undefined,
                })
              }
              searchable
              clearable
            />
            <MultiSelect
              label="Category"
              placeholder="Select categories..."
              data={allCategoryOptions}
              value={pendingFilters.categoryIds || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  categoryIds: values.length > 0 ? values : undefined,
                })
              }
              searchable
              clearable
            />
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Toggle Active Confirmation Modal */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            itemToToggle?.isActive ? "Deactivate Machine" : "Activate Machine"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {itemToToggle?.isActive ? "deactivate" : "activate"} the machine{" "}
              <strong>{itemToToggle?.name}</strong>?
            </Text>
            {itemToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated machines will no longer be available for selection.
              </Text>
            )}
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="default" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={itemToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggle}
                loading={toggleMachineActive.isPending}
              >
                {itemToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
